import React, { useEffect, useRef, useState } from "react";
import { Box, FormControl } from "@mui/material";
import { InputBase, InputLabel } from "components/common/Input";
import { loadGoogleMaps } from "utils/loadGoogleMaps";
import type { AddressFields, GeocoderAddressComponent } from "types/google-maps";

function parseAddressComponents(
  components: GeocoderAddressComponent[] | undefined,
  formattedAddress = ""
): AddressFields {
  const get = (type: string, useShort = false): string => {
    const c = components?.find((comp) => comp.types.includes(type));
    return c ? (useShort ? c.short_name : c.long_name) : "";
  };

  const streetNumber = get("street_number");
  const route = get("route");
  const subpremise = get("subpremise");
  const locality = get("locality") || get("sublocality") || get("sublocality_level_1");
  const state = get("administrative_area_level_1", true);
  const postalCode = get("postal_code");
  const postalSuffix = get("postal_code_suffix");
  const country = get("country", true) || get("country");

  const address =
    [streetNumber, route].filter(Boolean).join(" ").trim() ||
    formattedAddress.split(",")[0]?.trim() ||
    "";
  const address2 = subpremise;
  const zipcode = postalSuffix ? `${postalCode}-${postalSuffix}`.replace(/^-/, "") : postalCode;

  return {
    address,
    address2,
    city: locality,
    state,
    zipcode,
    country,
  };
}

export interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (fields: AddressFields | string) => void;
  singleLineMode?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Unique id when multiple address fields exist on one page (e.g. work order lines). */
  inputId?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  singleLineMode = false,
  label = "Address",
  placeholder,
  disabled,
  error,
  helperText,
  inputId = "address-autocomplete",
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<{
    getPlace(): import("types/google-maps").PlaceResult;
    addListener(event: string, handler: () => void): void;
  } | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onChangeRef = useRef(onChange);
  onAddressSelectRef.current = onAddressSelect;
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setScriptLoaded(true);
      })
      .catch(() => {
        // Do not enable input when script fails (e.g. missing API key)
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const g = window.google;
    if (!scriptLoaded || !inputRef.current || !g?.maps?.places) return;

    const Autocomplete = g.maps.places.Autocomplete;
    const input = inputRef.current;

    if (autocompleteRef.current) return;

    const autocomplete = new Autocomplete(input, {
      types: ["address"],
      fields: ["address_components", "formatted_address", "place_id"],
    });
    autocompleteRef.current = autocomplete;

    const handlePlaceChanged = () => {
      const place = autocomplete.getPlace();
      const formattedAddress = place.formatted_address || "";
      const onSelect = onAddressSelectRef.current;
      const onChangeCb = onChangeRef.current;

      if (!onSelect) return;

      if (place.address_components?.length) {
        const fields = parseAddressComponents(place.address_components, formattedAddress);
        if (singleLineMode) {
          onSelect(formattedAddress);
          onChangeCb(formattedAddress);
        } else {
          onSelect(fields);
          onChangeCb(fields.address);
        }
        return;
      }

      if (place.place_id) {
        const div = document.createElement("div");
        const service = new g.maps.places.PlacesService(div);
        service.getDetails(
          {
            placeId: place.place_id,
            fields: ["address_components", "formatted_address"],
          },
          (detail: import("types/google-maps").PlaceResult | null, status: string) => {
            if (status !== g.maps.places.PlacesServiceStatus.OK || !detail) {
              if (formattedAddress) {
                if (singleLineMode) {
                  onAddressSelectRef.current?.(formattedAddress);
                  onChangeRef.current(formattedAddress);
                } else {
                  const fallback = parseAddressComponents(undefined, formattedAddress);
                  onAddressSelectRef.current?.(fallback);
                  onChangeRef.current(fallback.address);
                }
              }
              return;
            }
            const components = detail.address_components;
            const fields = parseAddressComponents(
              components,
              detail.formatted_address || formattedAddress
            );
            if (singleLineMode) {
              onAddressSelectRef.current?.(detail.formatted_address || formattedAddress);
              onChangeRef.current(detail.formatted_address || formattedAddress);
            } else {
              onAddressSelectRef.current?.(fields);
              onChangeRef.current(fields.address);
            }
          }
        );
      }
    };

    autocomplete.addListener("place_changed", handlePlaceChanged);

    return () => {
      if (autocompleteRef.current && g?.maps?.event) {
        g.maps.event.clearInstanceListeners(autocomplete);
      }
      autocompleteRef.current = null;
    };
  }, [scriptLoaded, singleLineMode]);

  const isDisabled = disabled || !scriptLoaded;
  const displayPlaceholder = placeholder ?? (scriptLoaded ? "Start typing an address..." : "Loading...");

  return (
    <FormControl variant="standard" fullWidth error={error}>
      <InputLabel shrink htmlFor={inputId}>
        {label}
      </InputLabel>
      <InputBase
        id={inputId}
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={displayPlaceholder}
        disabled={isDisabled}
      />
      {helperText && (
        <Box component="span" sx={{ fontSize: 12, color: "error.main", mt: 0.5, display: "block" }}>
          {helperText}
        </Box>
      )}
    </FormControl>
  );
}
